import React, { useEffect, useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import CountryPicker from '../components/CountryPicker';
import { useUser } from '../context/UserContext';
import {
  fetchProjects,
  createProjectApi,
  fetchProjectTasks,
  createProjectTaskApi,
  updateProjectTaskApi,
  fetchTags,
  createTagApi
} from '../components/api';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, useDroppable, useDraggable } from '@dnd-kit/core';
import ProjectModal from '../components/ProjectModal';
import TaskModal from '../components/TaskModal';

const STATUS_COLUMNS = [
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'done', label: 'Done' }
];

const COLOR_PALETTE = [
  '#22d3ee', // cyan-400
  '#60a5fa', // blue-400
  '#a78bfa', // violet-400
  '#f472b6', // pink-400
  '#f59e0b', // amber-500
  '#34d399', // green-400
  '#f87171', // red-400
  '#10b981', // emerald-500
  '#fbbf24', // amber-400
  '#38bdf8'  // sky-400
];

function DroppableColumn({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`space-y-2 ${isOver ? 'ring-2 ring-cyan-400' : ''}`}>
      {children}
    </div>
  );
}

function DraggableTask({ task, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `task-${task.id}` });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return children({ setNodeRef, style, isDragging, listeners, attributes });
}

const Projects = ({ userClaims }) => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [filterCountryOption, setFilterCountryOption] = useState(null);
  const [currentView, setCurrentView] = useState('board'); // board | table | tags
  const [searchProject, setSearchProject] = useState('');
  const [activeTagFilters, setActiveTagFilters] = useState(new Set());
  const [newTagColor, setNewTagColor] = useState('');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const allowedCountriesIso3 = user?.allowedCountriesIso3 || [];
  const multipleCountries = allowedCountriesIso3.length !== 1 || allowedCountriesIso3.includes('global');
  const selectedIso3 = filterCountryOption?.iso3 || filterCountryOption?.value || (multipleCountries ? null : (allowedCountriesIso3[0] || null));
  const allowedCountriesIso3Key = useMemo(() => (Array.isArray(allowedCountriesIso3) ? allowedCountriesIso3.join(',') : ''), [allowedCountriesIso3]);
  const allowedCountriesIso3Ready = useMemo(() => Array.isArray(allowedCountriesIso3), [allowedCountriesIso3]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [prj, tagList] = await Promise.all([
          fetchProjects({ country_code: selectedIso3 || undefined }),
          fetchTags()
        ]);
        setProjects(prj);
        setTags(tagList);
        // Auto-select first project
        const defaultProjectId = prj && prj[0] ? prj[0].id : null;
        setActiveProjectId(defaultProjectId);
      } catch (e) {
        setError('Failed to load projects.');
      } finally {
        setLoading(false);
      }
    };
    // Only run when countries known
    if (allowedCountriesIso3Ready) {
      init();
    }
  }, [selectedIso3, allowedCountriesIso3Key, allowedCountriesIso3Ready]);

  useEffect(() => {
    const loadTasks = async () => {
      if (!activeProjectId) {
        setTasks([]);
        return;
      }
      try {
        const data = await fetchProjectTasks({ project_id: activeProjectId });
        setTasks(data);
      } catch (e) {
        setError('Failed to load tasks.');
      }
    };
    loadTasks();
  }, [activeProjectId]);

  const filteredProjects = useMemo(() => {
    const term = searchProject.trim().toLowerCase();
    return projects.filter(p => {
      const matchesCountry = selectedIso3 ? p.country_code === selectedIso3 : true;
      const matchesTerm = !term || (p.name?.toLowerCase().includes(term));
      return matchesCountry && matchesTerm;
    });
  }, [projects, selectedIso3, searchProject]);

  const visibleTasks = useMemo(() => {
    return tasks.filter(t => {
      if (!activeProjectId || t.project_id === activeProjectId) {
        if (activeTagFilters.size) {
          for (const tagId of activeTagFilters) {
            if (!(t.tags || []).includes(tagId)) return false;
          }
        }
        return true;
      }
      return false;
    });
  }, [tasks, activeProjectId, activeTagFilters]);

  const kpis = useMemo(() => {
    const total = visibleTasks.length;
    const done = visibleTasks.filter(t => t.status === 'done').length;
    const open = total - done;
    const byStatus = STATUS_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: visibleTasks.filter(t => t.status === c.key).length }), {});
    return { total, open, done, byStatus };
  }, [visibleTasks]);

  const handleCreateProject = () => {
    setEditingProject({ name: '', status: 'planned', owner_id: null, description: '', tags: [] });
    setShowProjectModal(true);
  };

  const handleCreateTask = () => {
    if (!activeProjectId) return;
    setEditingTask({ project_id: activeProjectId, title: '', status: 'todo', priority: 'medium', assignee_id: null, description: '', tags: [] });
    setShowTaskModal(true);
  };

  const openProjectModal = (project) => {
    setEditingProject(project);
    setShowProjectModal(true);
  };

  const openTaskModal = (task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const moveTask = async (taskId, nextStatus) => {
    try {
      const updated = await updateProjectTaskApi(taskId, { status: nextStatus });
      setTasks(prev => prev.map(t => (t.id === taskId ? updated : t)));
    } catch (_) {}
  };

  const toggleFilterTag = (tagId) => {
    setActiveTagFilters(prev => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
      return next;
    });
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active?.id || '');
    const taskId = Number(activeId.replace('task-', ''));
    const nextStatus = over?.id;
    if (!taskId || !nextStatus) return;
    if (!STATUS_COLUMNS.find(c => c.key === nextStatus)) return;
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status !== nextStatus) {
      moveTask(taskId, nextStatus);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const getFirstSentence = (html) => {
    if (!html) return '';
    try {
      const text = String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      if (!text) return '';
      const match = text.match(/[^.!?]+[.!?]/);
      const sentence = match ? match[0].trim() : text;
      const trimmed = sentence.length > 140 ? sentence.slice(0, 140).trim() + '…' : sentence;
      return trimmed;
    } catch (_) {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="projects" userClaims={userClaims} />
      <div className="p-4">
        <div className="bg-white shadow-md rounded-lg p-4">
          {error && (
            <div className="mb-3 bg-red-100 border border-red-300 text-red-800 px-3 py-2 rounded">
              {error}
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xl font-semibold">Projects</div>
            <div className="flex-1" />
            {multipleCountries && (
              <div className="w-64">
                <CountryPicker
                  value={filterCountryOption}
                  onChange={(opt) => setFilterCountryOption(opt)}
                  placeholder="All Countries"
                  isClearable
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentView('board')} className={`px-3 py-1 rounded ${currentView==='board'?'bg-cyan-500 text-white':'bg-gray-100'}`}>Board</button>
              <button onClick={() => setCurrentView('table')} className={`px-3 py-1 rounded ${currentView==='table'?'bg-cyan-500 text-white':'bg-gray-100'}`}>Table</button>
              <button onClick={() => setCurrentView('tags')} className={`px-3 py-1 rounded ${currentView==='tags'?'bg-cyan-500 text-white':'bg-gray-100'}`}>Tags</button>
            </div>
            <button onClick={handleCreateProject} className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-3 rounded">+ Project</button>
            <button onClick={handleCreateTask} className="bg-gray-800 hover:bg-black text-white font-semibold py-2 px-3 rounded">+ Task</button>
          </div>

          {/* KPIs */}
          <div className="mt-4 flex gap-3">
            <div className="bg-gray-50 border rounded px-3 py-2"><div className="text-sm">Items</div><div className="text-xl font-bold">{kpis.total}</div></div>
            <div className="bg-gray-50 border rounded px-3 py-2"><div className="text-sm">Open</div><div className="text-xl font-bold">{kpis.open}</div></div>
            <div className="bg-gray-50 border rounded px-3 py-2"><div className="text-sm">Done</div><div className="text-xl font-bold">{kpis.done}</div></div>
          </div>

          {/* Sidebar */}
          <div className="mt-4 grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-3">
              <div className="mb-2 text-xs text-gray-500 uppercase">Projects</div>
              <div className="flex gap-2 mb-2">
                <input value={searchProject} onChange={e=>setSearchProject(e.target.value)} placeholder="Search" className="flex-1 p-2 border rounded" />
              </div>
              <div className="max-h-[60vh] overflow-auto flex flex-col gap-2">
                {filteredProjects.map(p => (
                  <div key={p.id} className={`p-2 rounded border ${activeProjectId===p.id?'border-cyan-500 ring-1 ring-cyan-300':''} bg-white cursor-pointer`} onClick={()=>setActiveProjectId(p.id)}>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.country_code} • {p.status}</div>
                    {(Array.isArray(p.tags) && p.tags.length > 0) && (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {p.tags.map(tid => {
                          const tg = tags.find(x=>x.id===tid);
                          if (!tg) return null;
                          return <span key={tid} className="text-[10px] px-2 py-0.5 rounded border" style={{borderColor: tg.color || '#e5e7eb'}}>{tg.name}</span>;
                        })}
                      </div>
                    )}
                    {p.description && (
                      <div className="mt-1 text-xs text-gray-600">{getFirstSentence(p.description)}</div>
                    )}
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        className="text-xs px-2 py-1 bg-gray-100 rounded border"
                        onMouseDown={(e)=>e.stopPropagation()}
                        onTouchStart={(e)=>e.stopPropagation()}
                        onClick={(e)=>{e.stopPropagation(); openProjectModal(p);}}
                      >Edit</button>
                    </div>
                  </div>
                ))}
                {filteredProjects.length===0 && (
                  <div className="text-sm text-gray-500">No projects</div>
                )}
              </div>
              {/* Active tag filters */}
              <div className="mt-4">
                <div className="mb-2 text-xs text-gray-500 uppercase">Active filters</div>
                <div className="flex gap-2 flex-wrap">
                  {Array.from(activeTagFilters).length===0 ? (
                    <span className="text-xs px-2 py-1 bg-gray-100 rounded border">None</span>
                  ) : (
                    Array.from(activeTagFilters).map(id => {
                      const t = tags.find(x=>x.id===id);
                      return (
                        <button key={id} onClick={()=>toggleFilterTag(id)} className="text-xs px-2 py-1 rounded border" style={{borderColor: t?.color || '#e5e7eb'}}>
                          {t?.name || id}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="col-span-12 lg:col-span-9">
              {currentView === 'board' && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  {loading && (
                    <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
                  )}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    {STATUS_COLUMNS.map(col => (
                      <div key={col.key} className="bg-gray-50 border rounded p-2">
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-semibold">{col.label}</div>
                          <div className="text-xs bg-gray-200 px-2 py-0.5 rounded">{kpis.byStatus[col.key] || 0}</div>
                        </div>
                        <DroppableColumn id={col.key}>
                          {visibleTasks.filter(t => t.status === col.key).map(t => (
                            <DraggableTask key={t.id} task={t}>
                              {({ setNodeRef, style, isDragging, listeners, attributes }) => (
                              <div ref={setNodeRef} style={{ ...style, opacity: isDragging ? 0.7 : 1 }} className="bg-white border rounded p-2">
                                <div className="font-semibold">{t.title}</div>
                                {t.description && (
                                  <div className="text-xs text-gray-600 mt-1">{getFirstSentence(t.description)}</div>
                                )}
                                <div className="text-xs text-gray-500 flex gap-2 flex-wrap mt-1">
                                  <span className="px-2 py-0.5 bg-gray-100 rounded border">{t.priority || '—'}</span>
                                </div>
                                <div className="flex gap-2 flex-wrap mt-2">
                                  {(t.tags || []).map(tid => {
                                    const tg = tags.find(x=>x.id===tid);
                                    if (!tg) return null;
                                    return (
                                      <button key={tid} onMouseDown={(e)=>e.stopPropagation()} onTouchStart={(e)=>e.stopPropagation()} onClick={()=>toggleFilterTag(tid)} className="text-xs px-2 py-1 rounded border" style={{borderColor: tg.color || '#e5e7eb'}}>
                                        {tg.name}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div className="mt-2 flex gap-2">
                                  <button {...listeners} {...attributes} className="text-xs px-2 py-1 bg-gray-100 rounded border cursor-move">Drag</button>
                                  <button onMouseDown={(e)=>e.stopPropagation()} onTouchStart={(e)=>e.stopPropagation()} onClick={()=>moveTask(t.id, 'in_progress')} className="text-xs px-2 py-1 bg-gray-100 rounded border">Start ▶</button>
                                  <button onMouseDown={(e)=>e.stopPropagation()} onTouchStart={(e)=>e.stopPropagation()} onClick={()=>moveTask(t.id, 'done')} className="text-xs px-2 py-1 bg-gray-100 rounded border">Complete ✓</button>
                                  <button onMouseDown={(e)=>e.stopPropagation()} onTouchStart={(e)=>e.stopPropagation()} onClick={()=>openTaskModal(t)} className="text-xs px-2 py-1 bg-gray-100 rounded border">Edit ✎</button>
                                </div>
                              </div>
                              )}
                            </DraggableTask>
                          ))}
                        </DroppableColumn>
                      </div>
                    ))}
                  </div>
                </DndContext>
              )}

              {currentView === 'table' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Title</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Priority</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Project</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Tags</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {visibleTasks.map(t => {
                        const proj = projects.find(p=>p.id===t.project_id);
                        return (
                          <tr key={t.id}>
                            <td className="px-3 py-2 text-sm">{t.title}</td>
                            <td className="px-3 py-2 text-sm">{t.status}</td>
                            <td className="px-3 py-2 text-sm">{t.priority || '—'}</td>
                            <td className="px-3 py-2 text-sm">{proj?.name || '—'}</td>
                            <td className="px-3 py-2 text-sm">
                              <div className="flex gap-2 flex-wrap">
                                {(t.tags || []).map(tid => {
                                  const tg = tags.find(x=>x.id===tid);
                                  if (!tg) return null;
                                  return <span key={tid} className="text-xs px-2 py-1 rounded border" style={{borderColor: tg.color || '#e5e7eb'}}>{tg.name}</span>;
                                })}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {currentView === 'tags' && (
                <div>
                  <div className="text-sm text-gray-500 mb-2">Click a tag to toggle it as a filter.</div>
                  <div className="flex gap-2 flex-wrap">
                    {tags.map(t => (
                      <button key={t.id} onClick={()=>toggleFilterTag(t.id)} className={`text-xs px-2 py-1 rounded border ${activeTagFilters.has(t.id)?'bg-cyan-200':''}`} style={{borderColor: t.color || '#e5e7eb'}}>
                        {t.name}
                      </button>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-col gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <input id="newTagName" placeholder="New tag name" className="p-2 border rounded" />
                      <input
                        type="color"
                        aria-label="Pick color"
                        title="Pick color"
                        value={newTagColor || '#22d3ee'}
                        onChange={(e)=>setNewTagColor(e.target.value)}
                        className="h-10 w-12 p-1 border rounded"
                      />
                      <input
                        id="newTagColor"
                        placeholder="#hex color (optional)"
                        className="p-2 border rounded w-40"
                        value={newTagColor}
                        onChange={(e)=>setNewTagColor(e.target.value)}
                      />
                      <button className="px-3 py-2 bg-gray-800 text-white rounded" onClick={async()=>{
                        const name = document.getElementById('newTagName').value.trim();
                        const color = (newTagColor || '').trim();
                        if (!name) return;
                        try {
                          const tag = await createTagApi({ name, color: color || undefined });
                          setTags(prev => [...prev, tag]);
                          document.getElementById('newTagName').value = '';
                          setNewTagColor('');
                        } catch (_) {}
                      }}>Create Tag</button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {COLOR_PALETTE.map(c => (
                        <button
                          type="button"
                          key={c}
                          title={c}
                          onClick={()=>setNewTagColor(c)}
                          className={`h-8 w-8 rounded-full border ${newTagColor===c ? 'ring-2 ring-cyan-400' : ''}`}
                          style={{ backgroundColor: c, borderColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Modals */}
          <ProjectModal
            isOpen={showProjectModal}
            onClose={()=>setShowProjectModal(false)}
            project={editingProject}
            mode={editingProject && editingProject.id ? 'edit' : 'create'}
            defaultCountryIso3={selectedIso3 || projects.find(p=>p.id===activeProjectId)?.country_code || allowedCountriesIso3[0]}
            onSaved={(updated)=>{
              setProjects(prev => {
                const exists = prev.some(p => p.id === updated.id);
                const next = exists ? prev.map(p => p.id===updated.id ? { ...p, ...updated } : p) : [updated, ...prev];
                return next;
              });
              setActiveProjectId(updated.id);
            }}
          />
          <TaskModal
            isOpen={showTaskModal}
            onClose={()=>setShowTaskModal(false)}
            task={editingTask}
            mode={editingTask && editingTask.id ? 'edit' : 'create'}
            defaultProjectId={editingTask?.project_id || activeProjectId}
            projectName={projects.find(p=>p.id===editingTask?.project_id || activeProjectId)?.name}
            onSaved={(updated)=>{
              setTasks(prev => {
                const exists = prev.some(t => t.id === updated.id);
                return exists ? prev.map(t => t.id===updated.id ? { ...t, ...updated } : t) : [updated, ...prev];
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Projects;


